import {
  Column,
  GridContainer,
  Li,
  Row,
  Box,
  Ul,
  FlexBox,
  Spinner,
} from '@vp/swan';
import { useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { EmptyHistory } from './EmptyHistory';
import { PaginationWithEllipses } from '../pagination/PaginationWithEllipses';
import { GET_ORDER_SUMMARY } from '../../constants/reactQueryKeys';
import { getOrdersSummary } from '../../apis/bopApi';
import { ErrorHandler } from '../shared/ErrorHandler';
import { useSiteData } from '../../hooks/useSiteData';
import { pageViewOrderHistory } from '../../apis/trackingApi';
import { useShopperId } from '../../hooks/useShopperId';
import { usePaginationContext } from '../contexts/PaginationContext';
import { useTranslations } from '@vp/om-translations';
import { OrderSummaryProvider } from '../contexts/OrderSummaryContext';
import { Order } from './Order';
import { HistoryViewHeader } from './HistoryViewHeader';

export const OrderHistoryView = () => {
  const shopperId = useShopperId();
  const { vistacartTenant: vistacartTenantName, tenantEnvironment } =
    useSiteData();

  const [sharedActionMessages = {}] = useTranslations(['sharedActionMessages']);

  const {
    currentPageIndex,
    setTotalItemCount,
    numberOfItemsPerPage,
    totalPageCount,
  } = usePaginationContext();

  const { isLoading, isError, data, error, isSuccess } = useQuery({
    queryKey: [
      GET_ORDER_SUMMARY,
      currentPageIndex,
      shopperId,
      vistacartTenantName,
      tenantEnvironment,
      numberOfItemsPerPage,
    ],

    queryFn: () =>
      getOrdersSummary({
        environment: tenantEnvironment,
        offset: currentPageIndex,
        vistacartTenantName,
        shopperId,
        limit: numberOfItemsPerPage,
      }),

    /**
     * This prevents our current query data from being immediately invalidated on every refetch for a new page,
     * making page loads seamless. Read about it here: https://tanstack.com/query/latest/docs/framework/react/guides/paginated-queries
     */
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isSuccess) {
      setTotalItemCount(data?.total);
    }
  }, [data, isSuccess, setTotalItemCount]);

  const history = data?.orders;
  const isHistoryEmpty = !history || !history.length;

  // Fire a Segment page viewed event on page load
  useEffect(() => {
    pageViewOrderHistory(shopperId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the fetch fails, fire a NR event, show error message
  if (isError) {
    return <ErrorHandler error={error} />;
  }

  // While the fetch is loading, show a preloader
  if (isLoading) {
    return (
      <FlexBox justifyContent="center">
        <Spinner
          accessibleText={sharedActionMessages['loading']}
          aria-busy="true"
          aria-live="polite"
          role="status"
          size="super"
        />
      </FlexBox>
    );
  }

  // If the shopper has no orders, show empty history UX
  if (isHistoryEmpty) {
    return <EmptyHistory />;
  }

  // If the shopper has orders, show the full order history UX
  return (
    <>
      <GridContainer>
        <Row>
          <Column span={12}>
            <HistoryViewHeader showSearchBar />
          </Column>
        </Row>
        {/* Order list */}
        <Row>
          <Column span={12}>
            <Ul skin="minimal">
              {history.map((order, index) => (
                <Li key={order.orderNumber}>
                  <Box mb={7}>
                    <OrderSummaryProvider
                      order={order}
                      displayPosition={index + 1}
                    >
                      <Order />
                    </OrderSummaryProvider>
                  </Box>
                </Li>
              ))}
            </Ul>
          </Column>
        </Row>
      </GridContainer>
      {/* Pagination */}
      {totalPageCount > 1 && <PaginationWithEllipses />}
    </>
  );
};
